import { createClient } from '@supabase/supabase-js';
import { MedicationSubscriptionService } from './medication-subscription-service.js';
/**
 * PostgreSQL notification listener service for automatic subscription changes
 *
 * This service listens for database notifications when medication supply days change
 * and automatically transitions Stripe subscriptions between monthly/quarterly billing.
 */
export class MedicationSubscriptionListener {
    supabase;
    isListening = false;
    channel = null;
    constructor(supabaseUrl, supabaseServiceKey) {
        this.supabase = createClient(supabaseUrl, supabaseServiceKey, {
            auth: {
                autoRefreshToken: false,
                persistSession: false
            }
        });
    }
    /**
     * Start listening for PostgreSQL notifications
     */
    async startListening() {
        if (this.isListening) {
            console.log('🔔 Medication subscription listener already running');
            return;
        }
        try {
            console.log('🔔 Starting medication subscription listener...');
            // Create a real-time channel for PostgreSQL notifications
            this.channel = this.supabase.channel('medication-supply-changes', {
                config: {
                    broadcast: { self: true },
                    presence: { key: 'medication-listener' }
                }
            });
            // Listen for supply change notifications
            this.channel.on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'patient_medication_preferences'
            }, async (payload) => {
                await this.handleSupplyChange(payload);
            });
            // Subscribe to the channel
            const subscriptionResult = await this.channel.subscribe(async (status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('✅ Medication subscription listener connected successfully');
                    this.isListening = true;
                }
                else if (status === 'CHANNEL_ERROR') {
                    console.error('❌ Medication subscription listener channel error');
                    this.isListening = false;
                }
                else if (status === 'TIMED_OUT') {
                    console.error('❌ Medication subscription listener timed out');
                    this.isListening = false;
                }
                else if (status === 'CLOSED') {
                    console.log('🔌 Medication subscription listener connection closed');
                    this.isListening = false;
                }
            });
            console.log('🔔 Subscription result:', subscriptionResult);
        }
        catch (error) {
            console.error('❌ Failed to start medication subscription listener:', error);
            this.isListening = false;
            throw error;
        }
    }
    /**
     * Stop listening for PostgreSQL notifications
     */
    async stopListening() {
        if (!this.isListening) {
            console.log('🔔 Medication subscription listener not running');
            return;
        }
        try {
            console.log('🔔 Stopping medication subscription listener...');
            if (this.channel) {
                await this.supabase.removeChannel(this.channel);
                this.channel = null;
            }
            this.isListening = false;
            console.log('✅ Medication subscription listener stopped successfully');
        }
        catch (error) {
            console.error('❌ Failed to stop medication subscription listener:', error);
            throw error;
        }
    }
    /**
     * Handle supply day changes that trigger subscription transitions
     */
    async handleSupplyChange(payload) {
        try {
            console.log('🔄 Received medication supply change notification:', payload);
            const { new: newRecord, old: oldRecord, eventType } = payload;
            // Only process UPDATE events where supply_days changed
            if (eventType !== 'UPDATE' || !oldRecord || !newRecord) {
                console.log('🔍 Skipping non-update event or missing record data');
                return;
            }
            const oldSupplyDays = oldRecord.supply_days;
            const newSupplyDays = newRecord.supply_days;
            // Skip if supply_days didn't actually change
            if (oldSupplyDays === newSupplyDays) {
                console.log('🔍 Supply days unchanged, skipping');
                return;
            }
            // Skip if no active subscription
            if (!newRecord.stripe_subscription_id) {
                console.log('🔍 No active subscription, skipping');
                return;
            }
            console.log('🔄 Processing supply day change:', {
                patient_id: newRecord.patient_id,
                medication_id: newRecord.medication_id,
                oldSupplyDays,
                newSupplyDays,
                subscription_id: newRecord.stripe_subscription_id
            });
            // Determine old and new subscription types
            const oldType = oldSupplyDays <= 45 ? 'monthly' : 'quarterly';
            const newType = newSupplyDays <= 45 ? 'monthly' : 'quarterly';
            // Skip if subscription type didn't change
            if (oldType === newType) {
                console.log('🔍 Subscription type unchanged, skipping');
                return;
            }
            console.log(`🔄 Transitioning subscription from ${oldType} to ${newType}`);
            // Get medication information for price IDs
            const { data: medication, error: medError } = await this.supabase
                .from('medications')
                .select('*')
                .eq('id', newRecord.medication_id)
                .single();
            if (medError || !medication) {
                console.error('❌ Failed to get medication for subscription change:', medError);
                return;
            }
            // Use MedicationSubscriptionService to handle the transition
            const preference = {
                id: newRecord.id,
                patient_id: newRecord.patient_id,
                medication_id: newRecord.medication_id,
                medication_dosage_id: newRecord.medication_dosage_id,
                subscription_type: oldType,
                stripe_subscription_id: newRecord.stripe_subscription_id,
                supply_days: oldSupplyDays,
                next_prescription_due: newRecord.next_prescription_due,
                auto_refill: newRecord.auto_refill
            };
            await MedicationSubscriptionService.transitionSubscription(preference, newType, newSupplyDays, newRecord.medication_dosage_id);
            console.log('✅ Subscription transition completed successfully');
        }
        catch (error) {
            console.error('❌ Error handling medication supply change:', error);
            // Log detailed error for debugging
            console.error('❌ Supply change error details:', {
                error: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : null,
                payload
            });
        }
    }
    /**
     * Get the current listening status
     */
    get listening() {
        return this.isListening;
    }
    /**
     * Health check method to verify the listener is working
     */
    async healthCheck() {
        if (!this.isListening) {
            return {
                status: 'unhealthy',
                details: 'Listener not running'
            };
        }
        try {
            // Test database connection
            const { data, error } = await this.supabase
                .from('patient_medication_preferences')
                .select('count')
                .limit(1);
            if (error) {
                return {
                    status: 'unhealthy',
                    details: `Database connection failed: ${error.message}`
                };
            }
            return {
                status: 'healthy',
                details: 'Listener running and database accessible'
            };
        }
        catch (error) {
            return {
                status: 'unhealthy',
                details: `Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
            };
        }
    }
}
// Export a singleton instance for use across the application
let listenerInstance = null;
export const getMedicationSubscriptionListener = (supabaseUrl, supabaseServiceKey) => {
    if (!listenerInstance) {
        if (!supabaseUrl || !supabaseServiceKey) {
            throw new Error('Supabase URL and service key are required to initialize the medication subscription listener');
        }
        listenerInstance = new MedicationSubscriptionListener(supabaseUrl, supabaseServiceKey);
    }
    return listenerInstance;
};
// Graceful shutdown handling (Node.js only)
if (typeof process !== 'undefined' && process.on) {
    process.on('SIGTERM', async () => {
        console.log('🔔 SIGTERM received, stopping medication subscription listener...');
        if (listenerInstance) {
            await listenerInstance.stopListening();
        }
    });
    process.on('SIGINT', async () => {
        console.log('🔔 SIGINT received, stopping medication subscription listener...');
        if (listenerInstance) {
            await listenerInstance.stopListening();
        }
    });
}
